/**
 * Session-owned personal inference transport. Keeps WASM and WebGPU resources warm between
 * successful requests; reset, timeout, cancellation and transport errors destroy private memory.
 * The parent owns authentication and must reset when its user or token changes.
 * @module apps/web/src/lib/inferenceWorkerClient
 */
import type { InferenceBatch } from './browserInferenceModel';

/** One bounded message; credentials never enter the compute Worker. */
export interface InferenceWorkerRequest {
  /** Unique correlation identifier, checked again when a result arrives. */
  id: string;
  /** At most 2,048 activity feature records, processed in batches of at most 64. */
  targets: unknown[];
  /** At most 512 current-user observations; callers select authorized evidence. */
  observations: unknown[];
  /** Auto permits GPU selection; force_cpu bypasses GPU initialization. */
  policy: 'auto' | 'force_cpu';
}

/** Correlated result or failure, without any durable mutation capability. */
export interface InferenceWorkerReply {
  /** Identifier copied from the request. */
  id: string;
  /** Target-ordered batches when inference succeeds; absent on failure. */
  batches?: InferenceBatch[];
  /** Safe diagnostic text when inference cannot finish; absent on success. */
  error?: string;
}

/**
 * Warm-up message: load and initialize the WASM module without any personal data, so the first
 * real request skips module fetch and compilation. Sent only to pooled, never-used Workers.
 */
export interface InferenceWorkerWarmRequest {
  /** Discriminates warm-up from inference requests (which carry no `type`). */
  type: 'warm';
  /** Correlation identifier copied into the reply. */
  id: string;
}

/** Reply to a warm-up: `warmed` on success, `error` when the module could not load. */
export interface InferenceWorkerWarmReply {
  /** Identifier copied from the warm-up request. */
  id: string;
  /** True when the module is initialized. */
  warmed?: boolean;
  /** Safe diagnostic text when initialization failed. */
  error?: string;
}

/**
 * Warm a pooled inference Worker: post a warm-up and resolve when it reports the module loaded.
 * Uses `addEventListener` (not `onmessage`) so the client that later acquires the Worker keeps
 * full ownership of `onmessage`. Rejects on error replies, Worker errors or `signal` abort.
 */
export function warmInferenceWorker(worker: Worker, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const id = `warm:${crypto.randomUUID()}`;
    const done = () => {
      worker.removeEventListener('message', onMessage as EventListener);
      worker.removeEventListener('error', onError);
      signal?.removeEventListener('abort', onAbort);
    };
    const onMessage = (event: MessageEvent<InferenceWorkerWarmReply>) => {
      if (event.data?.id !== id) return;
      done();
      if (event.data.warmed) resolve();
      else reject(new Error(event.data.error || 'Browser inference warm-up failed.'));
    };
    const onError = () => {
      done();
      reject(new Error('Browser inference worker failed during warm-up.'));
    };
    const onAbort = () => {
      done();
      reject(signal?.reason ?? new Error('Browser inference warm-up cancelled.'));
    };
    if (signal?.aborted) return onAbort();
    worker.addEventListener('message', onMessage as EventListener);
    worker.addEventListener('error', onError);
    signal?.addEventListener('abort', onAbort, { once: true });
    try {
      worker.postMessage({ type: 'warm', id } satisfies InferenceWorkerWarmRequest);
    } catch {
      onError();
    }
  });
}

/** Pending work is limited to one request; duplicate callers cannot build an unbounded queue. */
interface PendingInference {
  /** Identifier of the one accepted request. */
  id: string;
  /** Resolve only a correlated reply from the currently owned Worker. */
  resolve: (batches: InferenceBatch[]) => void;
  /** Reject timeout, cancellation, reset or computation failures. */
  reject: (error: Error) => void;
  /** Remove deadline and abort listener on every terminal path. */
  cleanup: () => void;
}

/** Persistent transport with no retained evidence outside the private Worker after a request. */
export class InferenceWorkerClient {
  /** Lazily created Worker; reused only after a successful response. */
  private worker?: Worker;
  /** Exclusive request slot; callers retry after it settles. */
  private pending?: PendingInference;

  /** Inject Worker creation and a bounded deadline to make lifecycle/error behavior testable. */
  constructor(
    /** Creates an isolated browser Worker, never a shared global/SharedWorker. */
    private readonly factory: () => Worker,
    /** Total request deadline including initialization and all batches; defaults to 15 seconds. */
    private readonly timeoutMs = 15_000
  ) {}

  /** Drop private WASM/GPU memory and reject in-flight work before another session can reuse it. */
  reset(reason = 'Your inference session changed.'): void {
    this.worker?.terminate();
    this.worker = undefined;
    const pending = this.pending;
    this.pending = undefined;
    pending?.cleanup();
    pending?.reject(new Error(reason));
  }

  /**
   * Send one bounded request without credential material. Overlap is rejected instead of queued;
   * abort terminates computation even if synchronous WASM cannot process a cancellation message.
   */
  infer(
    targets: unknown[],
    observations: unknown[],
    signal?: AbortSignal
  ): Promise<InferenceBatch[]> {
    if (signal?.aborted) return Promise.reject(new Error('Browser inference cancelled.'));
    if (this.pending) return Promise.reject(new Error('Browser inference is already running.'));
    if (targets.length > 2_048 || observations.length > 512)
      return Promise.reject(new Error('Browser inference input limit exceeded.'));
    if (!targets.length) return Promise.resolve([]);
    try {
      if (!this.worker) {
        const worker = this.factory();
        this.worker = worker;
        worker.onmessage = (event: MessageEvent<InferenceWorkerReply>) => {
          if (this.worker !== worker || event.data.id !== this.pending?.id) return;
          const pending = this.pending;
          if (event.data.error || !Array.isArray(event.data.batches)) {
            this.reset(event.data.error || 'Browser inference returned an invalid reply.');
            return;
          }
          this.pending = undefined;
          pending.cleanup();
          pending.resolve(event.data.batches);
        };
        worker.onerror = () => {
          if (this.worker === worker) this.reset('Browser inference worker failed.');
        };
        worker.onmessageerror = () => {
          if (this.worker === worker) this.reset('Browser inference reply could not be decoded.');
        };
      }
    } catch {
      return Promise.reject(new Error('Browser inference worker could not start.'));
    }
    const worker = this.worker;
    return new Promise((resolve, reject) => {
      const id = crypto.randomUUID();
      const abort = () => this.reset('Browser inference cancelled.');
      const timer = setTimeout(() => this.reset('Browser inference timed out.'), this.timeoutMs);
      signal?.addEventListener('abort', abort, { once: true });
      this.pending = {
        id,
        resolve,
        reject,
        cleanup: () => {
          clearTimeout(timer);
          signal?.removeEventListener('abort', abort);
        },
      };
      try {
        worker.postMessage({
          id,
          targets,
          observations,
          policy: 'auto',
        } satisfies InferenceWorkerRequest);
      } catch {
        this.reset('Browser inference request could not be sent.');
      }
    });
  }
}
